import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import { Button, Chip, ChipGroup, TextField } from '@nyce/admin-kit/ui';
import { useDebounced } from '@nyce/admin-kit/console';
import {
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { formatBytes, formatDateTime } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { ApiError, saveBlob } from '@nyce/admin-kit/api';
import { LOG_LEVELS, printQueue, type LogEntriesPage, type LogEntry, type LogLevel } from '../api/client';
import { pqPath, printServersOrNull, usePrintQueueTarget, usePrintQueueWrite } from '../shared';
import '../printqueue.css';

const PAGE = 200;

const SOURCE_LABELS: Record<string, string> = {
  printserver: 'Print server',
  engine: 'Print engine',
  bootstrapper: 'Bootstrapper',
  nlog: 'NLog internal',
};
const sourceLabel = (s: string) => SOURCE_LABELS[s] ?? s;

/** Warnings and worse first: what an operator looks for. */
const LEVEL_LABELS: Record<LogLevel, string> = {
  trace: 'Trace',
  debug: 'Debug',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
  critical: 'Critical',
};

const csv = (v: string) => v.split(',').filter(Boolean);

/**
 * A print server's fetched logs read in the hub (print queue 1.12.0, print server 3.1.0): the print server's log, its
 * print engines' output, the bootstrapper's log and NLog's internal log as one list, newest first, filtered by source,
 * level, text and print job. `?tenant=&server=` like the print server page; `source`, `level`, `q`, `job` in the URL.
 */
export function PrintQueuePrintServerLogsPage() {
  const target = usePrintQueueTarget();
  const api = useMemo(() => printQueue(target), [target]);
  const canWrite = usePrintQueueWrite();
  const toast = useNotice();
  const [tenant] = useParam('tenant', '');
  const [server] = useParam('server', '');
  const [sourceKey, setSourceKey] = useParam('source', '');
  const [levelKey, setLevelKey] = useParam('level', '');
  const [text, setText] = useParam('q', '');
  const [jobKey, setJobKey] = useParam('job', '');
  const [draft, setDraft] = useState(text);
  const debounced = useDebounced(draft, 300);
  useEffect(() => setText(debounced.trim()), [debounced, setText]);

  const serverPath = pqPath(
    `/printqueue/printserver?tenant=${encodeURIComponent(tenant)}&server=${encodeURIComponent(server)}`,
    target
  );
  const row = useAsync(async () => {
    const servers = await printServersOrNull(api, tenant);
    return (
      servers?.find((s) => s.tenantId === tenant && s.name.toLowerCase() === server.toLowerCase()) ?? null
    );
  }, [api, tenant, server]);
  const version = row.data?.logsCreatedAt ?? null;

  const [page, setPage] = useState<LogEntriesPage | null>(null);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const sources = csv(sourceKey);
  const levels = csv(levelKey);
  const job = jobKey ? Number(jobKey) : undefined;
  const filterKey = `${version}|${sourceKey}|${levelKey}|${text}|${jobKey}`;

  const load = async (offset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const p = await api.logEntries(tenant, row.data?.name ?? server, {
        v: version,
        sources,
        levels,
        q: text,
        job,
        offset,
        limit: PAGE,
      });
      setPage(p);
      setEntries((cur) => (offset ? [...cur, ...p.entries] : p.entries));
      setMissing(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMissing(true);
      else setError((e as Error).message);
      setEntries([]);
      setPage(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (row.loading || row.data?.logsState !== 'ready') return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, row.loading]);

  const download = async () => {
    try {
      const f = await api.downloadLogs(tenant, row.data?.name ?? server);
      saveBlob(f.blob, f.fileName);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const count = (list: { name: string; count: number }[] | undefined, name: string) =>
    list?.find((x) => x.name === name)?.count;
  const ready = row.data?.logsState === 'ready';

  return (
    <Page
      title={
        <>
          <Link to={pqPath('/printqueue/printservers', target)} className="nyk-back">
            Print servers
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          <Link to={serverPath}>{server}</Link>
          <span className="nyk-crumb-sep"> / </span>
          Logs
        </>
      }
      description={
        ready && row.data
          ? [
              row.data.logsDays && `${row.data.logsDays} day${row.data.logsDays === 1 ? '' : 's'}`,
              row.data.logsSize != null && formatBytes(row.data.logsSize),
              row.data.logsCreatedAt && `uploaded ${formatDateTime(row.data.logsCreatedAt)}`,
              page && `${page.total} lines`,
            ]
              .filter(Boolean)
              .join(', ')
          : 'The log files a print server sent when asked (Get logs… on its page).'
      }
      actions={
        ready && (
          <span className="nyk-inline">
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw size={14} />}
              loading={loading}
              onClick={() => void load()}
            >
              Refresh
            </Button>
            {canWrite && (
              <Button size="sm" variant="ghost" icon={<Download size={14} />} onClick={() => void download()}>
                Download zip
              </Button>
            )}
          </span>
        )
      }
    >
      {toast.element}
      <ErrorBox error={row.error ?? error} />
      <Loading loading={row.loading} />
      {!row.loading && !row.error && (!ready || missing) && (
        <EmptyState
          title="No logs fetched for this print server."
          hint={
            row.data?.logsState === 'requested' || row.data?.logsState === 'sent'
              ? 'The print server has been asked and has not answered yet.'
              : 'Ask for them with Get logs… on the print server page; they are kept 7 days.'
          }
          action={<Link to={serverPath}>Open {server}</Link>}
        />
      )}
      {ready && !missing && (
        <>
          {page?.info && (
            <details className="pq-logs-info">
              <summary>
                What the print server sent ({page.files.length} file{page.files.length === 1 ? '' : 's'}
                {page.truncated ? ', the oldest lines left out' : ''})
              </summary>
              <pre>{page.info}</pre>
            </details>
          )}
          <div className="pq-toolbar">
            <TextField
              aria-label="Search"
              className="nyk-search"
              type="search"
              placeholder="Text contains"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="pq-status-filter">
              <ChipGroup
                aria-label="Source"
                options={(page?.sources ?? []).map((s) => ({
                  value: s.name,
                  label: `${sourceLabel(s.name)} (${s.count})`,
                }))}
                value={sources}
                onChange={(v) => setSourceKey(v.join(','))}
              />
            </div>
            <div className="pq-status-filter">
              <ChipGroup
                aria-label="Level"
                options={LOG_LEVELS.filter((l) => count(page?.levels, l)).map((l) => ({
                  value: l,
                  label: `${LEVEL_LABELS[l]} (${count(page?.levels, l)})`,
                }))}
                value={levels}
                onChange={(v) => setLevelKey(v.join(','))}
              />
              {job != null && (
                <Chip size="sm" selected onClick={() => setJobKey('')} title="Show every line again">
                  Print job {job} ×
                </Chip>
              )}
            </div>
          </div>
          <div className="nyk-card">
            <Loading loading={loading && !entries.length} />
            {!!entries.length && (
              <TableScroller>
                <table className="nyk-ui-table nyk-cards pq-logs" data-testid="pq-log-entries">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Level</th>
                      <th>Source</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <LogRow key={`${i}-${e.time}`} entry={e} onJob={(id) => setJobKey(String(id))} />
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            )}
            {!loading && page && !entries.length && <EmptyState title="No log lines match." />}
            {page && entries.length < page.matched && (
              <div className="pq-more">
                <Button size="sm" variant="ghost" loading={loading} onClick={() => void load(entries.length)}>
                  Load older ({page.matched - entries.length} more)
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </Page>
  );
}

/** One line; a multi-line message (a stack trace) opens on click. */
function LogRow({ entry, onJob }: { entry: LogEntry; onJob: (job: number) => void }) {
  const [open, setOpen] = useState(false);
  const [first, ...rest] = entry.message.split('\n');
  return (
    <tr className={`pq-log pq-log-${entry.level}`}>
      <td data-label="Time" className="pq-log-time">
        {entry.time ? formatLogTime(entry.time) : '–'}
      </td>
      <td data-label="Level">
        <span className={`pq-log-level pq-log-level-${entry.level}`}>
          {LEVEL_LABELS[entry.level] ?? entry.level}
        </span>
      </td>
      <td data-label="Source">
        {sourceLabel(entry.source)}
        {entry.job != null && (
          <button
            type="button"
            className="nyk-link pq-log-job"
            onClick={() => onJob(entry.job!)}
            title="Only this print job"
          >
            job {entry.job}
          </button>
        )}
      </td>
      <td data-label="Message" className="pq-log-message">
        {first}
        {rest.length > 0 && (
          <>
            {' '}
            <button
              type="button"
              className="nyk-link"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
            >
              {open ? 'less' : `+${rest.length} line${rest.length === 1 ? '' : 's'}`}
            </button>
            {open && <pre>{rest.join('\n')}</pre>}
          </>
        )}
      </td>
    </tr>
  );
}

/** The print server's own clock (its offset kept), to the millisecond: what the files say. */
export function formatLogTime(iso: string) {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.(\d{1,3}))?/.exec(iso);
  return m ? `${m[1]} ${m[2]}.${(m[3] ?? '0').padEnd(3, '0')}` : iso;
}
